"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 25: Konami VRC4b (Gradius II)
 * ------------------------------------------------------------------ */
// The VRC4 is a Konami ASIC with 8K PRG windows, 1K CHR windows and a
// CPU-cycle IRQ. The four registers of each $1000-aligned page are selected
// by two address lines, and the VRC4b variant (plus the VRC2c, also tagged 25
// by iNES) swaps them against A0/A1:
//
//   offset 0 -> register 0    offset 2 -> register 1
//   offset 1 -> register 2    offset 3 -> register 3
//
// which puts the chip's registers at:
//
//   $8000     PRG bank at $8000 (or $C000 with the swap mode set)
//   $9000     nametable arrangement      $9001  PRG swap mode / WRAM control
//   $A000     PRG bank at $A000
//   $B000/$B002  CHR 0 low/high  $B001/$B003  CHR 1 low/high  (PPU $0000/$0400)
//   $C000/$C002  CHR 2 low/high  $C001/$C003  CHR 3 low/high  (PPU $0800/$0C00)
//   $D000/$D002  CHR 4 low/high  $D001/$D003  CHR 5 low/high  (PPU $1000/$1400)
//   $E000/$E002  CHR 6 low/high  $E001/$E003  CHR 7 low/high  (PPU $1800/$1C00)
//   $F000     IRQ latch low    $F001  IRQ control    $F002  IRQ latch high
//   $F003     IRQ acknowledge
//
// A CHR bank is nine bits: the low register holds bits 3-0 of the value and
// the high register its low nibble shifted into bits 8-4. Gradius II splits a
// bank number by LSRing it four times for the high register. The two IRQ latch
// halves work the same way, so the game's per-frame `STA $F000; STY $F002`
// pair (Y holding the high nibble) puts a scanline count like $80 in the
// latch. The high and low halves both read the bottom four data bits, and
// that split was verified against the game's own code rather than assumed.
//
// The VRC4 IRQ is not a scanline counter: it is an 8-bit counter behind a
// 114/114/113-cycle prescaler, clocked by CPU cycles. NES.frame() feeds the
// real cycle count through the same watchesCpuCycles hook the FME-7 uses.
// ------------------------------------------------------------------ */

// VRC4b wiring: A0 and A1 are swapped on the board, so the register number is
// the two low address bits exchanged.
function vrc4bRegister(address) {
  var low = address & 3;
  return ((low & 1) << 1) | ((low >> 1) & 1);
}

function register(Mappers) {
  Mappers[25] = function (nes) {
    this.nes = nes;
  };

  Mappers[25].prototype = new Mappers[0]();

  // The VRC4 counts CPU cycles (through a 114/114/113 prescaler), not PPU
  // scanlines, so NES.frame() hands it the real cycle count per CPU burst.
  Mappers[25].prototype.watchesCpuCycles = true;

  // The board drives its own nametable arrangement: the $9000 register, not
  // the iNES header, owns it. loadROM still seeds the register from the header
  // so a board that never writes $9000 keeps the declared arrangement.
  Mappers[25].prototype.ownsMirroring = true;

  Mappers[25].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // PRG: the $8000 and $A000 windows are switchable 8K; $C000 and $E000 are
    // fixed to the second-to-last and last 8K banks unless the swap mode moves
    // the $8000 register to $C000.
    this.prgBank0 = 0; // 5 bits
    this.prgBank1 = 0; // 5 bits
    this.prgSwap = false; // $9001 bit 1
    this.wramEnabled = false; // $9001 bit 0: 2 KB WRAM at $6000-$6FFF

    // CHR: eight 9-bit 1K banks for PPU $0000-$1FFF.
    this.chrBanks = [0, 0, 0, 0, 0, 0, 0, 0];

    // $9000 nametable arrangement, kept in the chip's own encoding
    // (0 = vertical, 1 = horizontal, 2/3 = one-screen lower/upper).
    this.mirroring = 0;

    // IRQ: an 8-bit up-counting counter behind a CPU-cycle prescaler.
    this.irqLatch = 0;
    this.irqCounter = 0;
    this.irqMode = 0; // $F001 bit 2: 0 = scanline prescaler, 1 = cycle
    this.irqEnabled = false; // $F001 bit 1
    this.irqEnableAck = false; // $F001 bit 0: copied into E by an ack
    this.irqPrescaler = 341; // scanline mode: drawn down 3 per CPU cycle
  };

  Mappers[25].prototype.write = function (address, value) {
    if (address < 0x8000) {
      // $6000-$7FFF is two 2 KB WRAM pages once $9001 bit 0 turns them on;
      // until then the window is open bus, so writes go nowhere.
      if (address >= 0x6000 && !this.wramEnabled) {
        return;
      }
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    var page = address & 0xf000;
    switch (page) {
      case 0x8000:
        // PRG Select 0: the 8K window at $8000, or at $C000 under the swap mode.
        if (vrc4bRegister(address) === 0) {
          this.prgBank0 = value & 0x1f;
          this.updatePrgBanks();
        }
        break;

      case 0x9000:
        switch (vrc4bRegister(address)) {
          case 0:
            // Nametable arrangement.
            this.mirroring = value & 0x03;
            this.applyMirroring();
            break;
          case 2:
            // PRG swap mode + WRAM control. Bit 0 turns the 2 KB WRAM at
            // $6000-$6FFF on, bit 1 swaps the $8000 and $C000 windows.
            this.wramEnabled = (value & 1) !== 0;
            this.prgSwap = (value & 2) !== 0;
            this.updatePrgBanks();
            break;
        }
        break;

      case 0xa000:
        // PRG Select 1: the 8K window at $A000.
        if (vrc4bRegister(address) === 0) {
          this.prgBank1 = value & 0x1f;
          this.updatePrgBanks();
        }
        break;

      case 0xf000:
        this.writeIrq(address & 3, value);
        break;

      default:
        // $B000-$EFFF: four 1K CHR windows per page, each with a low and a
        // high register. Register r of the window pair selects the 1K bank at
        // (page * 2 + r / 2) * $400, and even r is the low nibble, odd r the
        // high nibble.
        if (page >= 0xb000 && page <= 0xe000) {
          var reg = vrc4bRegister(address);
          var window = ((page - 0xb000) >> 12) * 2 + (reg >> 1);
          if (reg & 1) {
            this.chrBanks[window] =
              (this.chrBanks[window] & 0x0f) | ((value & 0x1f) << 4);
          } else {
            this.chrBanks[window] =
              (this.chrBanks[window] & 0x1f0) | (value & 0x0f);
          }
          this.load1kVromBank(this.chrBanks[window], window * 0x400);
        }
        break;
    }
  };

  // The IRQ page: $F000 latch low, $F001 control, $F002 latch high, $F003
  // acknowledge, one register per offset (the swap works out to no-op here).
  Mappers[25].prototype.writeIrq = function (reg, value) {
    switch (reg) {
      case 0:
        // Latch low nibble.
        this.irqLatch = (this.irqLatch & 0xf0) | (value & 0x0f);
        break;

      case 1:
        // Latch high nibble, from the bottom four data bits.
        this.irqLatch = (this.irqLatch & 0x0f) | ((value & 0x0f) << 4);
        break;

      case 2:
        // Control: M (bit 2), E (bit 1), A (bit 0). Any write acknowledges the
        // pending IRQ and resets the prescaler; an enable that went high also
        // reloads the counter from the latch.
        this.irqMode = (value >> 2) & 1;
        this.irqEnabled = (value >> 1) & 1;
        this.irqEnableAck = value & 1;
        this.irqPrescaler = 341;
        if (this.irqEnabled) {
          this.irqCounter = this.irqLatch;
        }
        break;

      default:
        // Acknowledge: the A bit is copied into E, so writing $F003 with A set
        // in the last control write keeps the IRQ armed ("repeated"), or
        // disarms it ("one-shot") when A was clear.
        this.irqEnabled = this.irqEnableAck;
        break;
    }
  };

  Mappers[25].prototype.clockCpuCycles = function (cycles) {
    // Disabled, the VRC4 does not clock either the prescaler or the counter.
    if (!this.irqEnabled) {
      return;
    }

    if (this.irqMode === 1) {
      for (var i = 0; i < cycles; i++) {
        this.irqStep();
      }
      return;
    }

    // Scanline mode: a 114/114/113-cycle prescaler. nesdev's simple form keeps
    // the countdown in one value: it starts at 341 and drops 3 per cycle, and
    // each underflow is one 113.67-cycle scanline and clocks the counter.
    var prescaler = this.irqPrescaler;
    for (var j = 0; j < cycles; j++) {
      prescaler -= 3;
      if (prescaler <= 0) {
        prescaler += 341;
        this.irqStep();
      }
    }
    this.irqPrescaler = prescaler;
  };

  // One counter clock: increment, and a wrap from $FF reloads the latch and
  // trips the IRQ line. The line stays asserted (the CPU re-requests on every
  // wrap) until the game acknowledges.
  Mappers[25].prototype.irqStep = function () {
    if (this.irqCounter === 0xff) {
      this.irqCounter = this.irqLatch;
      if (this.irqEnabled) {
        this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
      }
    } else {
      this.irqCounter++;
    }
  };

  Mappers[25].prototype.updatePrgBanks = function () {
    var count8k = this.nes.rom.romCount * 2; // romCount is 16K banks
    this.load8kRomBank(count8k - 1, 0xe000); // last, fixed
    if (this.prgSwap) {
      this.load8kRomBank(count8k - 2, 0x8000);
      this.load8kRomBank(this.prgBank0, 0xc000);
    } else {
      this.load8kRomBank(this.prgBank0, 0x8000);
      this.load8kRomBank(count8k - 2, 0xc000);
    }
    this.load8kRomBank(this.prgBank1, 0xa000);
  };

  Mappers[25].prototype.updateChrBanks = function () {
    for (var i = 0; i < 8; i++) {
      this.load1kVromBank(this.chrBanks[i], i * 0x400);
    }
  };

  Mappers[25].prototype.applyMirroring = function () {
    var types = this.nes.ppu.MIRRORING_TYPES;
    var rom = this.nes.rom;
    switch (this.mirroring) {
      case 0:
        this.setMirroring(rom.VERTICAL_MIRRORING);
        break;
      case 1:
        this.setMirroring(rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(types.SINGLE_SCREEN);
        break;
      default:
        this.setMirroring(types.SINGLE_SCREEN_ALT);
        break;
    }
  };

  Mappers[25].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("VRC4b: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on bank layout: every register is zero, the last 8K bank is fixed
    // at $E000 (where the reset vector lives) and the second-to-last at $C000.
    this.updatePrgBanks();
    this.updateChrBanks();

    // Seed the arrangement from the header. Gradius II writes $9000 = 1
    // (horizontal, matching its header) during init either way.
    this.mirroring =
      this.nes.rom.getMirroringType() === this.nes.rom.HORIZONTAL_MIRRORING
        ? 1
        : 0;
    this.applyMirroring();

    this.resetIrqHandler();
  };

  Mappers[25].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgBank0 = this.prgBank0;
    s.prgBank1 = this.prgBank1;
    s.prgSwap = this.prgSwap;
    s.wramEnabled = this.wramEnabled;
    s.chrBanks = this.chrBanks.slice();
    s.mirroring = this.mirroring;
    s.irqLatch = this.irqLatch;
    s.irqCounter = this.irqCounter;
    s.irqMode = this.irqMode;
    s.irqEnabled = this.irqEnabled;
    s.irqEnableAck = this.irqEnableAck;
    s.irqPrescaler = this.irqPrescaler;
    return s;
  };

  Mappers[25].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgBank0 = s.prgBank0;
    this.prgBank1 = s.prgBank1;
    this.prgSwap = s.prgSwap;
    this.wramEnabled = s.wramEnabled;
    this.chrBanks = s.chrBanks.slice();
    this.mirroring = s.mirroring;
    this.irqLatch = s.irqLatch;
    this.irqCounter = s.irqCounter;
    this.irqMode = s.irqMode;
    this.irqEnabled = s.irqEnabled;
    this.irqEnableAck = s.irqEnableAck;
    this.irqPrescaler = s.irqPrescaler;
    // The CPU's copy of the ROM windows and the PPU's copy of the pattern
    // windows are not part of the mapper's state, so re-derive them.
    this.updatePrgBanks();
    this.updateChrBanks();
    this.applyMirroring();
  };
}

module.exports = { register: register };
